<template>
  <div class="admin-payment-management">
    <a-card title="Управление статусами оплаты игроков" :bordered="false">
      <!-- Фильтры -->
      <a-form layout="inline" style="margin-bottom: 16px">
        <a-form-item label="Тип оплаты">
          <a-select
            v-model:value="selectedPaymentType"
            style="width: 200px"
            placeholder="Выберите тип оплаты"
            @change="handlePaymentTypeChange"
            :loading="loadingPaymentTypes"
          >
            <a-select-option
              v-for="paymentType in availablePaymentTypes"
              :key="paymentType.id"
              :value="paymentType.id"
            >
              {{ paymentType.name }} 
              <span v-if="paymentType.amount">({{ paymentType.amount }} ₽)</span>
            </a-select-option>
          </a-select>
        </a-form-item>
        
        <a-form-item label="Месяц" v-if="selectedPaymentType && isSubscriptionType">
          <a-select
            v-model:value="selectedMonth"
            style="width: 200px"
            placeholder="Выберите месяц"
            @change="handleMonthChange"
          >
            <a-select-option
              v-for="month in availableMonths"
              :key="month.value"
              :value="month.value"
            >
              {{ month.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>

      <!-- Кнопка отправки в бот -->
      <div style="margin: 16px 0; display: flex; justify-content: center; gap: 8px;">
        <a-button 
          type="primary"
          @click="sendToTelegram"
          :loading="sendingToTelegram"
          :disabled="!selectedPaymentType || players.length === 0"
          style="height: 32px; padding: 0 16px; font-size: 12px;"
        >
          <SendOutlined />
          Отправить в бот
        </a-button>
        
        <a-button 
          type="warning"
          @click="sendNotificationsToUnpaid"
          :loading="sendingNotifications"
          :disabled="!selectedPaymentType || summary.unpaid === 0"
          style="height: 32px; padding: 0 16px; font-size: 12px;"
        >
          <BellOutlined />
          Уведомить неоплатившим ({{ summary.unpaid }})
        </a-button>
      </div>

      <!-- Статистика -->
      <div style="margin-bottom: 12px; padding: 8px 12px; background: #fafafa; border-radius: 4px;">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px;">
          <div style="text-align: center;">
            <div style="font-size: 9px; color: #666;">Общая сумма</div>
            <div style="font-size: 10px; font-weight: bold; color: #1890ff;">{{ totalAllAmount || 0 }} ₽</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 9px; color: #666;">Оплаты</div>
            <div style="font-size: 14px; font-weight: bold; color: #52c41a;">{{ summary.completed }}</div>
            <div style="font-size: 10px; font-weight: bold; color: #52c41a;">{{ totalPaymentsAmount || 0 }} ₽</div>
          </div>
          <div style="text-align: center;">
            <div style="font-size: 9px; color: #666;">Не оплатили</div>
            <div style="font-size: 14px; font-weight: bold; color: #ff4d4f;">{{ summary.unpaid }}</div>
            <div style="font-size: 10px; font-weight: bold; color: #ff4d4f;">{{ totalUnpaidAmount || 0 }} ₽</div>
          </div>
        </div>
      </div>

      <!-- Таблица оплат -->
      <div style="margin-bottom: 16px;">
        <div style="margin-bottom: 8px;">
          <h4 style="margin: 0; font-size: 14px;">Список оплат ({{ players.length }} записей)</h4>
        </div>
        
        <!-- Заголовки групп -->
        <div v-if="players.length > 0" style="margin-bottom: 6px;">
          <div v-for="(group, groupIndex) in groupedPlayers" :key="`group-${groupIndex}`" 
               :style="{
                 background: '#f5f5f5',
                 padding: '8px 12px',
                 fontWeight: '600',
                 color: '#262626',
                 fontSize: '12px',
                 borderRadius: '4px',
                 marginBottom: '4px',
                 borderLeft: `3px solid ${getGroupHeaderColor(group.status)}`
               }">
            {{ getGroupTitle(group.status) }} ({{ group.count }})
          </div>
        </div>
        
        <!-- Таблица с данными - выходит за границы -->
        <div style="position: relative; overflow: visible;">
          <table class="fixed-table" style="width: calc(100vw - 32px); margin-left: calc(-50vw + 50%); border-collapse: collapse; border: 1px solid #e8e8e8; background: white; font-size: 12px; position: relative; z-index: 1;">
            <thead>
              <tr style="background: #fafafa;">
                <th style="border: 1px solid #e8e8e8; padding: 6px 4px; text-align: left; font-weight: 600; color: #262626; font-size: 11px; min-width: 120px;">Игрок</th>
                <th style="border: 1px solid #e8e8e8; padding: 6px 4px; text-align: left; font-weight: 600; color: #262626; font-size: 11px; min-width: 100px;">Сумма</th>
                <th style="border: 1px solid #e8e8e8; padding: 6px 4px; text-align: left; font-weight: 600; color: #262626; font-size: 11px; min-width: 100px;">Дата</th>
                <th style="border: 1px solid #e8e8e8; padding: 6px 4px; text-align: left; font-weight: 600; color: #262626; font-size: 11px; min-width: 120px;">Комментарий</th>
                <th style="border: 1px solid #e8e8e8; padding: 6px 4px; text-align: center; font-weight: 600; color: #262626; font-size: 11px; width: 50px;">Действия</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(payment, index) in players" :key="`player_${index}_${payment?.player_id || 'unknown'}`">
                <tr v-if="payment" style="border: 1px solid #e8e8e8; transition: background-color 0.2s;" :style="{ 
                  backgroundColor: getRowBackgroundColor(payment.payment_status, index),
                  borderBottom: isLastInGroup(index) ? '2px solid #d9d9d9' : '1px solid #e8e8e8'
                }">
                  <td style="border: 1px solid #e8e8e8; padding: 6px 4px;" :style="{ backgroundColor: getRowBackgroundColor(payment.payment_status, index) }">
                    <div style="font-weight: 500; font-size: 12px;">{{ payment.last_name }} {{ payment.first_name }}</div>
                  </td>

                <td style="border: 1px solid #e8e8e8; padding: 6px 4px;">
                  <div style="display: flex; align-items: center; gap: 4px;">
                    <span v-if="payment.amount" style="font-weight: 500; font-size: 12px;">{{ payment.amount }} ₽</span>
                    <span v-else style="color: #8c8c8c; font-size: 12px;">-</span>
                    <a-button
                      v-if="payment.receipt_url"
                      size="small"
                      type="link"
                      @click="viewReceipt(payment.receipt_url)"
                      style="margin: 0; padding: 0; min-width: 14px; height: 14px;"
                      title="Просмотреть квитанцию"
                    >
                      <template #icon>
                        <EyeOutlined style="font-size: 10px;" />
                      </template>
                    </a-button>
                  </div>
                </td>
                <td style="border: 1px solid #e8e8e8; padding: 6px 4px;">
                  <span v-if="payment.payment_created_at" style="color: #595959; font-size: 11px;">{{ formatDate(payment.payment_created_at) }}</span>
                  <span v-else style="color: #8c8c8c; font-size: 11px;">-</span>
                </td>
                <td style="border: 1px solid #e8e8e8; padding: 6px 4px;">
                  <div v-if="payment.is_exempt" style="color: #1890ff; font-size: 11px; font-weight: 500;">
                    <div>Освобожден</div>
                    <div v-if="payment.exempt_until_period" style="font-size: 10px; color: #8c8c8c;">
                      до {{ formatMonthLabel(payment.exempt_until_period) }}
                    </div>
                    <div v-else style="font-size: 10px; color: #faad14;">
                      Бессрочно
                    </div>
                    <div v-if="payment.exemption_reason" style="font-size: 10px; color: #595959; margin-top: 2px;">
                      {{ payment.exemption_reason }}
                    </div>
                  </div>
                  <div v-else>
                    <span v-if="payment.comment" style="color: #595959; font-size: 11px;">{{ payment.comment }}</span>
                    <span v-else style="color: #8c8c8c; font-size: 11px;">-</span>
                  </div>
                </td>
                <td style="border: 1px solid #e8e8e8; padding: 6px 4px; text-align: center;">
                  <a-button
                    size="small"
                    type="primary"
                    @click="openPaymentModal(payment)"
                    style="margin: 0; min-width: 20px; height: 20px; padding: 0;"
                    :title="payment.payment_id ? 'Редактировать оплату' : 'Добавить оплату'"
                  >
                    <template #icon>
                      <EditOutlined style="font-size: 9px;" />
                    </template>
                  </a-button>
                </td>
              </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </a-card>

    <!-- Модальное окно управления оплатами игрока -->
    <a-modal
      v-model:open="paymentModalVisible"
      title="Управление оплатами игрока"
      width="800px"
      :footer="null"
    >
      <div v-if="selectedPlayer" style="margin-bottom: 16px;">
        <h4>{{ selectedPlayer.last_name }} {{ selectedPlayer.first_name }}</h4>
        <p v-if="selectedPlayer.username" style="color: #8c8c8c; margin: 0;">@{{ selectedPlayer.username }}</p>
      </div>

      <!-- Существующие оплаты -->
      <div v-if="playerPayments.length > 0" style="margin-bottom: 24px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <h5 style="margin: 0;">Существующие оплаты</h5>
          <a-button
            v-if="selectedPlayer && selectedPlayer.payment_id"
            size="small"
            type="default"
            danger
            @click="deletePayment(selectedPlayer.payment_id)"
            style="margin-left: 8px;"
          >
            <DeleteOutlined />
            Удалить оплату
          </a-button>
        </div>
        <a-table
          :data-source="playerPayments"
          :columns="paymentColumns"
          :row-key="record => record.id ?? record.payment_id"
          :pagination="false"
          size="small"
          style="margin-bottom: 16px;"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'status'">
              <a-tag :color="getStatusColor(record.status)">
                {{ getStatusText(record.status) }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'amount'">
              <span v-if="record.amount">{{ record.amount }} ₽</span>
              <span v-else>-</span>
            </template>
            <template v-else-if="column.key === 'date'">
              {{ formatDate(record.created_at) }}
            </template>
            <template v-else-if="column.key === 'actions'">
              <a-space>
                <a-button
                  size="small"
                  type="link"
                  @click="editExistingPayment(record)"
                >
                  <EditOutlined />
                </a-button>
                <a-button
                  v-if="record.receipt_url"
                  size="small"
                  type="link"
                  @click="viewReceipt(record.receipt_url)"
                >
                  <EyeOutlined />
                </a-button>
                <a-button
                  size="small"
                  type="link"
                  danger
                  @click="deletePayment(record.id ?? record.payment_id)"
                >
                  <DeleteOutlined />
                </a-button>
              </a-space>
            </template>
          </template>
        </a-table>
      </div>

      <!-- Форма добавления новой оплаты -->
      <a-divider />
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; gap: 8px;">
        <h5 style="margin: 0;">{{ paymentForm.payment_id ? 'Редактирование оплаты' : 'Добавить новую оплату' }}</h5>
        <a-space>
          <a-button
            v-if="paymentForm.payment_id"
            size="small"
            type="default"
            @click="cancelEditingPayment"
          >
            Отменить редактирование
          </a-button>
          <a-button
            v-if="selectedPlayer && selectedPlayer.payment_id && playerPayments.length === 0"
            size="small"
            type="default"
            danger
            @click="deletePayment(selectedPlayer.payment_id)"
          >
            <DeleteOutlined />
            Удалить оплату
          </a-button>
        </a-space>
      </div>
      <a-alert
        v-if="paymentForm.payment_id"
        message="Вы редактируете существующую оплату"
        type="info"
        show-icon
        style="margin-bottom: 16px;"
      />
      <a-form :model="paymentForm" layout="vertical">
        <a-form-item label="Статус оплаты" required>
          <a-select
            v-model:value="paymentForm.status"
            placeholder="Выберите статус"
          >
            <a-select-option value="completed">Оплатил</a-select-option>
            <a-select-option value="pending">Ожидает проверки</a-select-option>
            <a-select-option value="rejected">Отклонён</a-select-option>
            <a-select-option value="unpaid">Не оплатил</a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="Сумма (руб.)">
          <a-input
            v-model:value="paymentForm.amount"
            type="number"
            min="0"
            placeholder="Введите сумму"
            @input="handleAmountInput"
          />
        </a-form-item>

        <a-form-item label="Комментарий">
          <a-textarea
            v-model:value="paymentForm.comment"
            :rows="3"
            placeholder="Введите комментарий (необязательно)"
          />
        </a-form-item>

        <a-form-item>
          <a-space>
            <a-button
              type="primary"
              @click="updatePaymentStatus"
              :loading="updating"
              :disabled="!paymentForm.status"
            >
              Добавить оплату
            </a-button>
            <a-button
              v-if="selectedPlayer && !selectedPlayer.is_exempt"
              type="default"
              @click="openExemptionModal(selectedPlayer)"
            >
              Освободить от оплаты
            </a-button>
            <a-button
              v-if="selectedPlayer && selectedPlayer.is_exempt"
              type="default"
              danger
              @click="removeExemption(selectedPlayer)"
            >
              Отменить освобождение
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- Модальное окно просмотра квитанции -->
    <a-modal
      v-model:open="receiptModalVisible"
      title="Квитанция об оплате"
      :footer="null"
      width="800px"
    >
      <div v-if="selectedReceiptUrl">
        <!-- Используем PdfViewer для PDF файлов -->
        <PdfViewer
          v-if="isPdfFile(selectedReceiptUrl)"
          :pdf-url="selectedReceiptUrl"
          :filename="'receipt.pdf'"
        />
        <!-- Для изображений -->
        <div v-else style="text-align: center;">
          <img
            :src="selectedReceiptUrl"
            alt="Квитанция"
            style="max-width: 100%; max-height: 400px; object-fit: contain;"
          />
          <div style="margin-top: 16px;">
            <a-button type="primary" @click="downloadReceipt">
              Скачать квитанцию
            </a-button>
          </div>
        </div>
      </div>
    </a-modal>

    <!-- Модальное окно освобождения от оплаты -->
    <a-modal
      v-model:open="exemptionModalVisible"
      title="Освобождение от оплаты"
      :confirm-loading="savingExemption"
      @ok="saveExemption"
      @cancel="cancelExemption"
      ok-text="Освободить"
      cancel-text="Отмена"
    >
      <div v-if="selectedPlayerForExemption">
        <p><strong>Игрок:</strong> {{ selectedPlayerForExemption.last_name }} {{ selectedPlayerForExemption.first_name }}</p>
        <p><strong>Тип оплаты:</strong> {{ getSelectedPaymentTypeName() }}</p>
        <p v-if="isSubscriptionType"><strong>Месяц:</strong> {{ formatMonthLabel(selectedMonth) }}</p>
        
        <a-divider />
        
        <a-form layout="vertical">
          <a-form-item label="Причина освобождения" required>
            <a-textarea
              v-model:value="exemptionForm.reason"
              :rows="4"
              placeholder="Например: Травма, отпуск, семейные обстоятельства..."
            />
          </a-form-item>
          
          <a-form-item v-if="isSubscriptionType">
            <a-checkbox v-model:checked="exemptionForm.isPermanent">
              Бессрочное освобождение
            </a-checkbox>
            <div style="color: #8c8c8c; font-size: 12px; margin-top: 4px;">
              При бессрочном освобождении игрок будет освобожден навсегда
            </div>
          </a-form-item>
          
          <a-form-item v-if="isSubscriptionType && !exemptionForm.isPermanent" label="Освободить до" required>
            <a-select
              v-model:value="exemptionForm.exemptUntilPeriod"
              placeholder="Выберите последний месяц освобождения"
            >
              <a-select-option
                v-for="month in futureMonths"
                :key="month.value"
                :value="month.value"
              >
                {{ month.label }}
              </a-select-option>
            </a-select>
            <div style="color: #8c8c8c; font-size: 12px; margin-top: 4px;">
              Освобождение будет действовать до конца выбранного месяца включительно
            </div>
          </a-form-item>
          
          <a-alert
            v-if="isSubscriptionType && exemptionForm.exemptUntilPeriod"
            :message="getExemptionDurationMessage()"
            type="info"
            show-icon
            style="margin-top: 8px;"
          />
        </a-form>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch, nextTick } from 'vue';
import { message } from 'ant-design-vue';
import { EditOutlined, EyeOutlined, DeleteOutlined, SendOutlined, BellOutlined } from '@ant-design/icons-vue';
import { api } from '../services/api';
import PdfViewer from './PdfViewer.vue';

export default {
  name: 'AdminPaymentManagement',
  components: {
    EditOutlined,
    EyeOutlined,
    DeleteOutlined,
    SendOutlined,
    PdfViewer,
    BellOutlined
  },
  props: {
    teamId: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    // Проверяем, что API доступен
    if (!api) {
      console.error('API service is not available!');
      throw new Error('API service is not available');
    }

    // Проверяем, что API методы доступны
    if (!api.getTeamPlayersPaymentStatuses || !api.updatePlayerPaymentStatus) {
      console.error('Required API methods are not available!');
      throw new Error('Required API methods are not available');
    }

    const loading = ref(false);
    const updating = ref(false);
    const players = ref([]);
    const selectedMonth = ref('');
    const selectedPaymentType = ref(null);
    const availablePaymentTypes = ref([]);
    const loadingPaymentTypes = ref(false);
    const sendingToTelegram = ref(false);
    const sendingNotifications = ref(false);
    
    // Используем глобальный флаг инициализации, чтобы избежать повторных инициализаций при перемонтировании
    if (!window.__adminPaymentManagementInitialized) {
      window.__adminPaymentManagementInitialized = {};
    }
    const initKey = `team_${props.teamId}`;
    const isInitialized = ref(window.__adminPaymentManagementInitialized[initKey] || false);
    const paymentModalVisible = ref(false);
    const selectedPlayer = ref(null);
    const playerPayments = ref([]);
    const receiptModalVisible = ref(false);
    const selectedReceiptUrl = ref('');
    
    // Exemption modal
    const exemptionModalVisible = ref(false);
    const selectedPlayerForExemption = ref(null);
    const savingExemption = ref(false);
    const exemptionForm = reactive({
      reason: '',
      exemptUntilPeriod: '',
      isPermanent: false
    });
    
    const paymentForm = reactive({
      status: '',
      amount: null,
      comment: '',
      payment_id: null
    });
    const currentEditingPaymentId = ref(null);
    const isCreatingNewPayment = ref(false);

    const summary = ref({
      total: 0,
      completed: 0,
      pending: 0,
      unpaid: 0,
      rejected: 0,
      exempt: 0
    });

    // Колонки для таблицы существующих оплат
    const paymentColumns = [
      {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        width: 120
      },
      {
        title: 'Сумма',
        dataIndex: 'amount',
        key: 'amount',
        width: 100
      },
      {
        title: 'Комментарий',
        dataIndex: 'comment',
        key: 'comment'
      },
      {
        title: 'Дата',
        dataIndex: 'created_at',
        key: 'date',
        width: 120
      },
      {
        title: 'Действия',
        key: 'actions',
        width: 80
      }
    ];

    // Подсчет общей суммы всех оплат
    const totalPaymentsAmount = computed(() => {
      if (!players.value || !players.value.length) return 0;
      
      try {
        const paymentsWithAmount = players.value.filter(player => 
          player && 
          player.payment_id && 
          player.amount && 
          parseFloat(player.amount) > 0
        );
        
        const total = paymentsWithAmount.reduce((sum, player) => {
          const amount = parseFloat(player.amount) || 0;
          return sum + amount;
        }, 0);
        
        return total;
      } catch (error) {
        console.error('Error calculating total payments amount:', error);
        return 0;
      }
    });

    // Подсчет суммы неоплат (для игроков без оплат)
    const totalUnpaidAmount = computed(() => {
      if (!players.value || !players.value.length) return 0;
      
      try {
        const unpaidPlayers = players.value.filter(player => 
          player &&
          !player.payment_id && 
          player.payment_status === 'unpaid'
        );
        
        // Предполагаем, что стандартная сумма абонемента 28000 руб (можно сделать настраиваемой)
        const standardAmount = 28000;
        const total = unpaidPlayers.length * standardAmount;
        return total;
      } catch (error) {
        console.error('Error calculating total unpaid amount:', error);
        return 0;
      }
    });

    // Общая сумма всех платежей (оплаченных + неоплаченных)
    const totalAllAmount = computed(() => {
      return totalPaymentsAmount.value + totalUnpaidAmount.value;
    });

    // Определяем, является ли выбранный тип оплаты абонементом
    const isSubscriptionType = computed(() => {
      if (!selectedPaymentType.value || !availablePaymentTypes.value.length) return false;
      const paymentType = availablePaymentTypes.value.find(pt => pt.id === selectedPaymentType.value);
      return paymentType && paymentType.type === 'subscription';
    });





    // Группировка игроков по статусу для отображения заголовков
    const groupedPlayers = computed(() => {
      if (!players.value || !players.value.length) return [];
      
      try {
        // Сортируем игроков: неоплатившие вверху, затем оплатившие (поздние вверху), затем освобождённые (ранние окончания вверху)
        const sortedPlayers = [...players.value].sort((a, b) => {
          // 1. Приоритет: неоплатившие вверху
          const aUnpaid = a.payment_status === 'unpaid';
          const bUnpaid = b.payment_status === 'unpaid';
          
          if (aUnpaid && !bUnpaid) return -1; // a (неоплативший) идет вверх
          if (!aUnpaid && bUnpaid) return 1;  // b (неоплативший) идет вверх
          
          // 2. Среди неоплативших сортируем по имени
          if (aUnpaid && bUnpaid) {
            return (a.last_name + a.first_name).localeCompare(b.last_name + b.first_name);
          }
          
          // 3. Среди оплативших сортируем по дате (самая поздняя вверху)
          const aPaid = a.payment_status === 'completed' || a.payment_status === 'pending' || a.payment_status === 'rejected';
          const bPaid = b.payment_status === 'completed' || b.payment_status === 'pending' || b.payment_status === 'rejected';
          
          if (aPaid && bPaid) {
            if (a.payment_created_at && b.payment_created_at) {
              return new Date(b.payment_created_at) - new Date(a.payment_created_at);
            }
            // Если у одного нет даты, он идет ниже
            if (a.payment_created_at && !b.payment_created_at) return -1;
            if (!a.payment_created_at && b.payment_created_at) return 1;
            return (a.last_name + a.first_name).localeCompare(b.last_name + b.first_name);
          }
          
          // 4. Среди освобождённых сортируем по дате окончания освобождения
          const aExempt = a.payment_status === 'exempt';
          const bExempt = b.payment_status === 'exempt';
          
          if (aExempt && bExempt) {
            // Бессрочные освобождения идут в конец
            const aPermanent = !a.exempt_until_period;
            const bPermanent = !b.exempt_until_period;
            
            if (aPermanent && !bPermanent) return 1;  // бессрочное идет вниз
            if (!aPermanent && bPermanent) return -1; // с периодом идет вверх
            
            // Если оба с периодом, сортируем по дате окончания (ранние вверху)
            if (!aPermanent && !bPermanent) {
              return a.exempt_until_period.localeCompare(b.exempt_until_period);
            }
            
            // Если оба бессрочные, сортируем по имени
            return (a.last_name + a.first_name).localeCompare(b.last_name + b.first_name);
          }
          
          // Смешанные случаи: оплатившие идут перед освобождёнными
          if (aPaid && bExempt) return -1;
          if (aExempt && bPaid) return 1;
          
          // Остальные случаи по имени
          return (a.last_name + a.first_name).localeCompare(b.last_name + b.first_name);
        });

        // Обновляем массив players отсортированными данными
        players.value.splice(0, players.value.length, ...sortedPlayers);

        // Определяем порядок статусов для группировки
        const statusOrder = ['unpaid', 'pending', 'rejected', 'completed', 'exempt'];
        
        const groups = [];
        
        statusOrder.forEach(status => {
          const playersWithStatus = players.value.filter(player => player && player.payment_status === status);
          if (playersWithStatus.length > 0) {
            groups.push({
              status: status,
              count: playersWithStatus.length,
              startIndex: players.value.findIndex(player => player && player.payment_status === status)
            });
          }
        });
        
        return groups;
      } catch (error) {
        console.error('Error grouping players:', error);
        return [];
      }
    });

    // Генерируем список месяцев (3 будущих + текущий + 12 прошлых)
    const availableMonths = computed(() => {
      const months = [];
      const currentDate = new Date();
      
      // Добавляем 3 будущих месяца
      for (let i = 3; i >= 1; i--) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
        const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const label = date.toLocaleDateString('ru-RU', { 
          month: 'long', 
          year: 'numeric' 
        });
        months.push({ value, label });
      }
      
      // Добавляем текущий месяц
      const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const currentValue = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
      const currentLabel = currentMonth.toLocaleDateString('ru-RU', { 
        month: 'long', 
        year: 'numeric' 
      });
      months.push({ value: currentValue, label: currentLabel });
      
      // Добавляем 12 прошлых месяцев
      for (let i = 1; i <= 12; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const label = date.toLocaleDateString('ru-RU', { 
          month: 'long', 
          year: 'numeric' 
        });
        months.push({ value, label });
      }
      
      return months;
    });

    // Генерируем список будущих месяцев для освобождений (текущий + 12 будущих)
    const futureMonths = computed(() => {
      const months = [];
      const currentDate = new Date();
      
      // Добавляем текущий + 12 будущих месяцев
      for (let i = 0; i <= 12; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
        const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const label = date.toLocaleDateString('ru-RU', { 
          month: 'long', 
          year: 'numeric' 
        });
        months.push({ value, label });
      }
      
      return months;
    });

    // Обработчик изменения типа оплаты
    const handlePaymentTypeChange = () => {
      if (!api || !api.token) {
        message.error('Ошибка авторизации. Пожалуйста, обновите страницу.');
        return;
      }
      
      // Для разовых платежей устанавливаем текущий месяц (для внутреннего использования)
      if (!isSubscriptionType.value) {
        const currentDate = new Date();
        selectedMonth.value = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      }
      
      // Загружаем данные для нового типа оплаты
      loadPlayersPaymentStatuses();
    };

    // Обработчик изменения месяца
    const handleMonthChange = () => {
      if (!api || !api.token) {
        message.error('Ошибка авторизации. Пожалуйста, обновите страницу.');
        return;
      }
      loadPlayersPaymentStatuses();
    };

    // Обработчик кнопки обновления
    const handleRefresh = () => {
      if (!api || !api.token) {
        message.error('Ошибка авторизации. Пожалуйста, обновите страницу.');
        return;
      }
      loadPlayersPaymentStatuses();
    };

    // Загрузка типов оплат команды
    const loadPaymentTypes = async (retryCount = 0) => {
      // Защита от повторных запросов
      if (loadingPaymentTypes.value) {
        console.log('Payment types request already in progress, skipping...');
        return;
      }

      try {
        loadingPaymentTypes.value = true;
        // Используем public endpoint для получения активных типов оплат
        const response = await api.getPublicTeamPaymentTypes(props.teamId);
        const allPaymentTypes = Array.isArray(response) ? response : [];
        
        // Фильтруем типы оплат - показываем только те, к которым есть права
        const paymentTypesWithRights = [];
        for (const paymentType of allPaymentTypes) {
          try {
            const canManage = await api.canManagePaymentType(props.teamId, paymentType.id);
            if (canManage && canManage.canManage) {
              paymentTypesWithRights.push(paymentType);
              console.log(`✅ User can manage payment type: ${paymentType.name} (ID: ${paymentType.id})`);
            }
          } catch (error) {
            console.warn(`Cannot check rights for payment type ${paymentType.id}:`, error);
          }
        }
        
        // Сохраняем только типы оплат с правами
        availablePaymentTypes.value = paymentTypesWithRights;
        
        // Автоматически выбираем первый тип оплаты из доступных
        if (availablePaymentTypes.value.length > 0 && !selectedPaymentType.value) {
          selectedPaymentType.value = availablePaymentTypes.value[0].id;
          console.log(`Auto-selected payment type: ${availablePaymentTypes.value[0].name}`);
        } else if (availablePaymentTypes.value.length === 0) {
          console.warn('No payment types with manage rights found');
          message.warning('У вас нет прав на управление типами оплат');
        }
      } catch (error) {
        console.error('Ошибка при загрузке типов оплат:', error);
        
        // Если ошибка 403 и это первая попытка, ждем обновления токена и пробуем снова
        if (error.message && error.message.includes('403') && retryCount === 0) {
          console.log('Got 403, waiting for token refresh and retrying...');
          loadingPaymentTypes.value = false;
          // Ждем 1 секунду для обновления токена
          await new Promise(resolve => setTimeout(resolve, 1000));
          return loadPaymentTypes(1); // Повторная попытка
        }
        
        message.error('Ошибка при загрузке типов оплат');
      } finally {
        loadingPaymentTypes.value = false;
      }
    };

    // Загрузка статусов оплаты игроков
    const loadPlayersPaymentStatuses = async () => {
      if (!selectedPaymentType.value) {
        message.warning('Выберите тип оплаты');
        return;
      }

      // Для абонементов проверяем месяц
      if (isSubscriptionType.value && !selectedMonth.value) {
        message.warning('Выберите месяц для абонемента');
        return;
      }

      // Проверяем API объект и токен
      if (!api || !api.token) {
        message.error('Ошибка авторизации. Пожалуйста, обновите страницу.');
        return;
      }

      // Защита от повторных запросов
      if (loading.value) {
        console.log('Request already in progress, skipping...');
        return;
      }

      loading.value = true;
      try {
        // Для абонементов используем месяц, для разовых - текущую дату
        const paymentPeriod = isSubscriptionType.value ? selectedMonth.value : new Date().toISOString().slice(0, 7);
        const data = await api.getTeamPlayersPaymentStatusesByType(props.teamId, selectedPaymentType.value, paymentPeriod);
        
        // Проверяем, что данные пришли правильно
        if (!data.players || !Array.isArray(data.players)) {
          message.error('Ошибка: неверный формат данных игроков');
          return;
        }
        
        if (!data.summary) {
          message.error('Ошибка: неверный формат сводки');
          return;
        }
        
        // Фильтруем null значения и добавляем вычисляемое поле player_name для отображения в таблице
        const playersWithDisplayName = data.players
          .filter(player => player !== null && player !== undefined)
          .map(player => ({
            ...player,
            player_name: `${player.last_name} ${player.first_name}`
          }));
        
        // Сортируем игроков по статусам в нужном порядке
        const statusOrder = ['unpaid', 'pending', 'rejected', 'completed', 'exempt'];
        const sortedPlayers = playersWithDisplayName.sort((a, b) => {
          const aIndex = statusOrder.indexOf(a.payment_status);
          const bIndex = statusOrder.indexOf(b.payment_status);
          if (aIndex === bIndex) {
            // Если статусы одинаковые, сортируем по фамилии
            return a.last_name.localeCompare(b.last_name);
          }
          return aIndex - bIndex;
        });
        
        // Принудительно обновляем данные
        players.value = [];
        await nextTick();
        
        // Дополнительная проверка: убираем null значения перед присвоением
        const validPlayers = sortedPlayers.filter(p => p !== null && p !== undefined);
        players.value = validPlayers;
        summary.value = data.summary;
        
        // Принудительно обновляем вычисляемые свойства
        await nextTick();
        
        console.log('Players loaded:', validPlayers.length);
        message.success('Данные обновлены');
      } catch (error) {
        message.error('Ошибка при загрузке данных');
      } finally {
        loading.value = false;
      }
    };

    // Открытие модального окна
    const openPaymentModal = async (player) => {
      selectedPlayer.value = player;
      isCreatingNewPayment.value = false;
      paymentForm.status = player.payment_status === 'exempt' ? 'unpaid' : player.payment_status;
      
      // Автоматически заполняем amount из выбранного типа оплаты
      const currentPaymentType = availablePaymentTypes.value.find(pt => pt.id === selectedPaymentType.value);
      paymentForm.amount = currentPaymentType?.amount || null;
      
      paymentForm.comment = '';
      paymentForm.payment_id = null;
      currentEditingPaymentId.value = null;
      paymentModalVisible.value = true;
      
      // Загружаем существующие оплаты игрока
      await loadPlayerPayments();
    };

    // Закрытие модального окна
    const closePaymentModal = () => {
      paymentModalVisible.value = false;
      selectedPlayer.value = null;
      playerPayments.value = [];
      paymentForm.status = '';
      paymentForm.amount = null;
      paymentForm.comment = '';
      paymentForm.payment_id = null;
      currentEditingPaymentId.value = null;
      isCreatingNewPayment.value = false;
    };

    // Загрузка всех оплат игрока
    const loadPlayerPayments = async () => {
      if (!selectedPlayer.value || !selectedPaymentType.value) {
        playerPayments.value = [];
        paymentForm.payment_id = null;
        currentEditingPaymentId.value = null;
        return;
      }
      
      try {
        let paymentPeriod = selectedMonth.value;
        if (!paymentPeriod || paymentPeriod === '') {
          const currentDate = new Date();
          paymentPeriod = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        }

        const data = await api.getPlayerPayments(
          props.teamId,
          selectedPlayer.value.player_id,
          selectedPaymentType.value,
          paymentPeriod
        );
        playerPayments.value = data.payments || [];

        if (playerPayments.value.length > 0) {
          const existingId = currentEditingPaymentId.value || paymentForm.payment_id;
          const findById = (id) => playerPayments.value.find(payment => {
            const paymentId = payment.id ?? payment.payment_id;
            return paymentId === id;
          });

          if (existingId) {
            const match = findById(existingId);
            if (match) {
              editExistingPayment(match, { preserveNotice: true, preserveMode: true });
              return;
            }
          }

          if (selectedPlayer.value && selectedPlayer.value.payment_id) {
            const match = findById(selectedPlayer.value.payment_id);
            if (match) {
              editExistingPayment(match, { preserveNotice: true, preserveMode: true });
              return;
            }
          }

          if (playerPayments.value.length === 1) {
            editExistingPayment(playerPayments.value[0], { preserveNotice: true, preserveMode: true });
          } else {
            paymentForm.payment_id = null;
            currentEditingPaymentId.value = null;
            isCreatingNewPayment.value = false;
          }
        } else {
          paymentForm.payment_id = null;
          currentEditingPaymentId.value = null;
          isCreatingNewPayment.value = false;
        }
      } catch (error) {
        console.error('Ошибка при загрузке оплат игрока:', error);
        playerPayments.value = [];
        paymentForm.payment_id = null;
        currentEditingPaymentId.value = null;
        isCreatingNewPayment.value = false;
      }
    };

    // Удаление оплаты
    const deletePayment = async (paymentId) => {
      try {
        await api.deletePayment(paymentId);
        message.success('Оплата удалена');
        
        // Перезагружаем данные
        await loadPlayerPayments();
        await loadPlayersPaymentStatuses();
        if (paymentForm.payment_id === paymentId) {
          paymentForm.payment_id = null;
          paymentForm.comment = '';
        }
        if (currentEditingPaymentId.value === paymentId) {
          currentEditingPaymentId.value = null;
        }
      } catch (error) {
        message.error('Ошибка при удалении оплаты');
      }
    };

    const editExistingPayment = (payment, options = {}) => {
      const paymentId = payment.id ?? payment.payment_id;
      paymentForm.payment_id = paymentId || null;
      currentEditingPaymentId.value = paymentId || null;
      paymentForm.status = payment.status || 'completed';
      paymentForm.amount = payment.amount ? Number(payment.amount) : null;
      paymentForm.comment = payment.comment || '';

      // Если дата платежа из другого месяца, обновляем выбранный месяц
      if (payment.payment_date) {
        const paymentMonth = payment.payment_date.slice(0, 7);
        if (paymentMonth && paymentMonth !== selectedMonth.value) {
          selectedMonth.value = paymentMonth;
        }
      }

      if (!options.preserveMode) {
        isCreatingNewPayment.value = false;
      }

      if (!options.preserveNotice) {
        message.info(`Редактирование платежа #${paymentId}`);
      }
    };

    const cancelEditingPayment = () => {
      paymentForm.payment_id = null;
      paymentForm.comment = '';
      const currentPaymentType = availablePaymentTypes.value.find(pt => pt.id === selectedPaymentType.value);
      paymentForm.amount = currentPaymentType?.amount || null;
      paymentForm.status = selectedPlayer.value
        ? (selectedPlayer.value.payment_status === 'exempt' ? 'unpaid' : selectedPlayer.value.payment_status)
        : '';
      currentEditingPaymentId.value = null;
      isCreatingNewPayment.value = true;
    };

    // Просмотр квитанции
    const viewReceipt = (receiptUrl) => {
      selectedReceiptUrl.value = receiptUrl;
      receiptModalVisible.value = true;
    };

    // Закрытие модального окна квитанции
    const closeReceiptModal = () => {
      receiptModalVisible.value = false;
      selectedReceiptUrl.value = '';
    };

    // Скачивание квитанции
    const downloadReceipt = () => {
      if (selectedReceiptUrl.value) {
        const link = document.createElement('a');
        link.href = selectedReceiptUrl.value;
        
        // Определяем расширение файла
        let filename = 'receipt';
        if (isPdfFile(selectedReceiptUrl.value)) {
          filename += '.pdf';
        } else {
          filename += '.jpg';
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    // Открытие модального окна освобождения
    const openExemptionModal = (player) => {
      selectedPlayerForExemption.value = player;
      exemptionForm.reason = player.exemption_reason || '';
      exemptionForm.exemptUntilPeriod = player.exempt_until_period || selectedMonth.value || '';
      exemptionForm.isPermanent = !player.exempt_until_period && player.is_exempt;
      exemptionModalVisible.value = true;
    };

    // Сохранение освобождения
    const saveExemption = async () => {
      if (!exemptionForm.reason || exemptionForm.reason.trim() === '') {
        message.warning('Укажите причину освобождения');
        return;
      }

      if (isSubscriptionType.value && !exemptionForm.isPermanent && !exemptionForm.exemptUntilPeriod) {
        message.warning('Выберите период, до которого действует освобождение, или отметьте бессрочное освобождение');
        return;
      }

      try {
        savingExemption.value = true;
        
        await api.updatePaymentExemption(props.teamId, selectedPaymentType.value, {
          playerId: selectedPlayerForExemption.value.player_id,
          exempt_from_payment: true,
          exemption_reason: exemptionForm.reason,
          exempt_until_period: exemptionForm.isPermanent ? null : exemptionForm.exemptUntilPeriod
        });

        message.success(exemptionForm.isPermanent ? 'Игрок освобожден от оплаты навсегда' : 'Игрок освобожден от оплаты');
        exemptionModalVisible.value = false;
        
        // Перезагружаем данные
        await loadPlayersPaymentStatuses();
      } catch (error) {
        console.error('Error saving exemption:', error);
        message.error('Ошибка при сохранении освобождения');
      } finally {
        savingExemption.value = false;
      }
    };

    // Отмена освобождения
    const removeExemption = async (player) => {
      try {
        await api.updatePaymentExemption(props.teamId, selectedPaymentType.value, {
          playerId: player.player_id,
          exempt_from_payment: false,
          exemption_reason: null,
          exempt_until_period: null
        });

        message.success('Освобождение отменено');
        
        // Перезагружаем данные
        await loadPlayersPaymentStatuses();
      } catch (error) {
        console.error('Error removing exemption:', error);
        message.error('Ошибка при отмене освобождения');
      }
    };

    // Закрытие модального окна освобождения
    const cancelExemption = () => {
      exemptionModalVisible.value = false;
      selectedPlayerForExemption.value = null;
      exemptionForm.reason = '';
      exemptionForm.exemptUntilPeriod = '';
      exemptionForm.isPermanent = false;
    };

    // Получить название выбранного типа оплаты
    const getSelectedPaymentTypeName = () => {
      const paymentType = availablePaymentTypes.value.find(pt => pt.id === selectedPaymentType.value);
      return paymentType ? paymentType.name : '';
    };

    // Получить сообщение о длительности освобождения
    const getExemptionDurationMessage = () => {
      if (!exemptionForm.exemptUntilPeriod || !selectedMonth.value) return '';
      
      const currentPeriod = selectedMonth.value;
      const untilPeriod = exemptionForm.exemptUntilPeriod;
      
      // Парсим периоды
      const [currentYear, currentMonthNum] = currentPeriod.split('-').map(Number);
      const [untilYear, untilMonthNum] = untilPeriod.split('-').map(Number);
      
      // Вычисляем количество месяцев
      const monthsDiff = (untilYear - currentYear) * 12 + (untilMonthNum - currentMonthNum);
      
      if (monthsDiff === 0) {
        return 'Освобождение только на текущий месяц';
      } else if (monthsDiff === 1) {
        return 'Освобождение на 2 месяца (текущий + следующий)';
      } else if (monthsDiff > 1) {
        return `Освобождение на ${monthsDiff + 1} ${getMonthsWord(monthsDiff + 1)}`;
      } else {
        return 'Период освобождения уже прошел';
      }
    };

    // Склонение слова "месяц"
    const getMonthsWord = (count) => {
      const lastDigit = count % 10;
      const lastTwoDigits = count % 100;
      
      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return 'месяцев';
      } else if (lastDigit === 1) {
        return 'месяц';
      } else if (lastDigit >= 2 && lastDigit <= 4) {
        return 'месяца';
      } else {
        return 'месяцев';
      }
    };

    // Отправка таблицы в Telegram
    const sendToTelegram = async () => {
      if (!selectedPaymentType.value || !selectedMonth.value) {
        message.warning('Выберите тип оплаты и месяц');
        return;
      }

      try {
        sendingToTelegram.value = true;

        const paymentType = availablePaymentTypes.value.find(pt => pt.id === selectedPaymentType.value);
        const paymentTypeName = paymentType ? paymentType.name : 'Платеж';
        
        await api.sendPaymentTableToTelegram(props.teamId, selectedPaymentType.value, selectedMonth.value, {
          paymentTypeName: paymentTypeName
        });

        message.success('Таблица отправлена в Telegram бот');
      } catch (error) {
        console.error('Error sending to Telegram:', error);
        message.error('Ошибка при отправке таблицы в бот');
      } finally {
        sendingToTelegram.value = false;
      }
    };

    // Отправка уведомлений неоплатившим
    const sendNotificationsToUnpaid = async () => {
      if (!selectedPaymentType.value) {
        message.warning('Выберите тип оплаты');
        return;
      }

      const unpaidPlayers = players.value.filter(p => p.payment_status === 'unpaid');
      if (unpaidPlayers.length === 0) {
        message.warning('Нет неоплатившихся игроков');
        return;
      }

      try {
        sendingNotifications.value = true;

        const paymentType = availablePaymentTypes.value.find(pt => pt.id === selectedPaymentType.value);
        const paymentTypeName = paymentType ? paymentType.name : 'Платеж';
        
        await api.sendPaymentReminderNotifications(props.teamId, selectedPaymentType.value, selectedMonth.value, {
          paymentTypeName: paymentTypeName,
          unpaidCount: unpaidPlayers.length
        });

        message.success(`Уведомления отправлены ${unpaidPlayers.length} игроку(ам)`);
      } catch (error) {
        console.error('Error sending notifications:', error);
        message.error('Ошибка при отправке уведомлений');
      } finally {
        sendingNotifications.value = false;
      }
    };

    // Обработка ввода суммы
    const handleAmountInput = (event) => {
      const value = event.target.value;
      const cleanValue = value.replace(/[^0-9]/g, '');
      paymentForm.amount = cleanValue ? parseInt(cleanValue) : null;
    };

    // Обновление статуса оплаты
    const updatePaymentStatus = async () => {
      if (!paymentForm.status) {
        message.warning('Выберите статус');
        return;
      }

      if (!api || !api.token) {
        message.error('Ошибка авторизации. Пожалуйста, обновите страницу.');
        return;
      }

      updating.value = true;
      try {
        // Валидация данных
        if (!paymentForm.amount || paymentForm.amount <= 0) {
          message.error('Пожалуйста, укажите сумму оплаты');
          updating.value = false;
          return;
        }
        
        if (!paymentForm.status) {
          message.error('Пожалуйста, выберите статус оплаты');
          updating.value = false;
          return;
        }
        
        // Определяем период оплаты
        let paymentPeriod = selectedMonth.value;
        
        // Если период не выбран (для разовых платежей), используем текущий месяц
        if (!paymentPeriod || paymentPeriod === '') {
          const currentDate = new Date();
          paymentPeriod = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
          console.log('Payment period not set, using current month:', paymentPeriod);
        }
        
        const updateData = {
          payment_type_id: selectedPaymentType.value,
          payment_period: paymentPeriod,
          payment_month: paymentPeriod, // For backward compatibility
          status: paymentForm.status,
          amount: Number(paymentForm.amount), // Явно преобразуем в число
          comment: paymentForm.comment || ''
        };
        
        if (paymentForm.payment_id) {
          updateData.payment_id = paymentForm.payment_id;
        }
        
        if (!updateData.payment_id && currentEditingPaymentId.value) {
          updateData.payment_id = currentEditingPaymentId.value;
        }

        const hasExistingPayments = playerPayments.value && playerPayments.value.length > 0;
        const isEditingExisting = !!updateData.payment_id;
        if (!isEditingExisting && hasExistingPayments && !isCreatingNewPayment.value) {
          message.warning('Выберите оплату из списка или нажмите "Отменить редактирование", чтобы создать новую запись.');
          updating.value = false;
          return;
        }
        
        console.log('Updating payment status:', {
          playerId: selectedPlayer.value.player_id,
          data: updateData
        });

        await api.updatePlayerPaymentStatus(selectedPlayer.value.player_id, updateData);

        message.success(paymentForm.payment_id ? 'Оплата обновлена' : 'Оплата добавлена');
        closePaymentModal();
        await loadPlayersPaymentStatuses(); // Перезагружаем данные
      } catch (error) {
        console.error('Error updating payment status:', error);
        let errorMessage = 'Ошибка при добавлении оплаты';
        if (error.message) {
          errorMessage += ': ' + error.message;
        }
        message.error(errorMessage);
      } finally {
        updating.value = false;
      }
    };

    // Получение цвета статуса
    const getStatusColor = (status) => {
      const colors = {
        completed: '#52c41a',
        pending: '#faad14',
        rejected: '#ff4d4f',
        unpaid: '#8c8c8c',
        exempt: '#1890ff'
      };
      return colors[status] || '#8c8c8c';
    };

    // Получение текста статуса
    const getStatusText = (status) => {
      const texts = {
        completed: 'Оплаты',
        pending: 'Ожидает проверки',
        rejected: 'Отклонён',
        unpaid: 'Не оплатил',
        exempt: 'Освобождён'
      };
      return texts[status] || status;
    };

    // Форматирование даты
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // Форматирование месяца
    const formatMonthLabel = (monthValue) => {
      if (!monthValue) return '';
      const [year, month] = monthValue.split('-');
      const date = new Date(year, parseInt(month) - 1);
      return date.toLocaleDateString('ru-RU', {
        month: 'long',
        year: 'numeric'
      });
    };

    // Получение цвета фона строки
    const getRowBackgroundColor = (status, index) => {
      const baseColors = {
        'unpaid': '#fff2f0',
        'pending': '#fffbe6',
        'rejected': '#fff1f0',
        'completed': '#f6ffed',
        'exempt': '#f0f5ff'
      };
      
      const color = baseColors[status] || '#fafafa';
      
      // Чередуем оттенки для лучшей читаемости
      if (index % 2 === 0) {
        return color;
      } else {
        // Слегка темнее для четных строк
        return color === '#fafafa' ? '#f5f5f5' : color;
      }
    };



    // Получение заголовка группы
    const getGroupTitle = (status) => {
      const titles = {
        'unpaid': 'Не оплатили',
        'pending': 'Ожидают проверки',
        'rejected': 'Отклонены',
        'completed': 'Оплатили',
        'exempt': 'Освобождены'
      };
      return titles[status] || status;
    };

    // Получение цвета заголовка группы
    const getGroupHeaderColor = (status) => {
      const colors = {
        'unpaid': '#ff4d4f',      // Красный для неоплативших
        'pending': '#faad14',     // Желтый для ожидающих проверки
        'rejected': '#ff7875',    // Светло-красный для отклоненных
        'completed': '#52c41a',   // Зеленый для оплативших
        'exempt': '#8c8c8c'      // Серый для освобожденных
      };
      return colors[status] || '#1890ff';
    };

    // Проверка, является ли строка последней в группе
    const isLastInGroup = (playerIndex) => {
      if (playerIndex === players.value.length - 1) return true;
      
      const currentPlayer = players.value[playerIndex];
      const nextPlayer = players.value[playerIndex + 1];
      
      if (!currentPlayer || !nextPlayer) return true;
      
      return currentPlayer.payment_status !== nextPlayer.payment_status;
    };

    // Проверка, является ли файл PDF
    const isPdfFile = (url) => {
      if (!url) return false;
      return url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('pdf');
    };

    // Инициализация
    onMounted(async () => {
      // Защита от повторных инициализаций
      if (isInitialized.value) {
        console.log('AdminPaymentManagement already initialized, skipping...');
        return;
      }
      
      console.log('Initializing AdminPaymentManagement...');
      isInitialized.value = true;
      window.__adminPaymentManagementInitialized[initKey] = true;
      
      // Устанавливаем текущий месяц по умолчанию
      const currentDate = new Date();
      selectedMonth.value = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      
      // Ждем следующего тика Vue
      await nextTick();
      
      // Даем время на обновление токена если нужно (небольшая задержка)
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Проверяем API объект и токен
      if (!api || !api.token) {
        console.warn('API object or token is not available!');
        message.error('Ошибка авторизации. Пожалуйста, обновите страницу.');
        return;
      }
      
      try {
        // Сначала загружаем типы оплат
        await loadPaymentTypes();
        
        // Затем загружаем статусы оплаты игроков
        if (selectedPaymentType.value) {
          loadPlayersPaymentStatuses();
        }
      } catch (error) {
        console.error('Error during initialization:', error);
        message.error('Ошибка при инициализации компонента');
      }
    });

    // Watch for team changes
    watch(() => props.teamId, (newTeamId, oldTeamId) => {
      if (newTeamId && newTeamId !== oldTeamId) {
        console.log('Team changed, reloading data...');
        // Сброс инициализации для старой команды
        if (oldTeamId) {
          const oldInitKey = `team_${oldTeamId}`;
          window.__adminPaymentManagementInitialized[oldInitKey] = false;
        }
        // Сброс инициализации для новой команды
        const newInitKey = `team_${newTeamId}`;
        window.__adminPaymentManagementInitialized[newInitKey] = false;
        isInitialized.value = false;
        // Перезагрузка данных
        setTimeout(async () => {
          await loadPaymentTypes();
          if (selectedPaymentType.value) {
            loadPlayersPaymentStatuses();
          }
        }, 100);
      }
    });

    return {
      loading,
      updating,
      players,
      selectedMonth,
      selectedPaymentType,
      availablePaymentTypes,
      loadingPaymentTypes,
      sendingToTelegram,
      sendingNotifications,
      isInitialized,
      isSubscriptionType,
      paymentModalVisible,
      selectedPlayer,
      paymentForm,
      summary,
      availableMonths,
      futureMonths,
      playerPayments,
      paymentColumns,
      receiptModalVisible,
      selectedReceiptUrl,
      exemptionModalVisible,
      selectedPlayerForExemption,
      savingExemption,
      exemptionForm,
      handleMonthChange,
      handlePaymentTypeChange,
      handleRefresh,
      loadPlayersPaymentStatuses,
      loadPaymentTypes,
      openPaymentModal,
      closePaymentModal,
      loadPlayerPayments,
      editExistingPayment,
      cancelEditingPayment,
      deletePayment,
      viewReceipt,
      closeReceiptModal,
      downloadReceipt,
      openExemptionModal,
      saveExemption,
      removeExemption,
      cancelExemption,
      getSelectedPaymentTypeName,
      getExemptionDurationMessage,
      sendToTelegram,
      sendNotificationsToUnpaid,
      handleAmountInput,
      updatePaymentStatus,
      getStatusColor,
      getStatusText,
      formatDate,
      formatMonthLabel,
      getRowBackgroundColor,
      groupedPlayers,
      getGroupTitle,
      getGroupHeaderColor,
      isLastInGroup,
      isPdfFile,
      totalPaymentsAmount,
      totalUnpaidAmount,
      totalAllAmount,
      currentEditingPaymentId,
      isCreatingNewPayment
    };
  }
};
</script>

  <style scoped>
  .admin-payment-management {
    padding: 16px;
  }
  
  .ant-statistic {
    text-align: center;
  }
  
  .ant-table {
    margin-top: 16px;
  }
  
  /* Убираем отступы у ant-card-body для таблицы */
  :deep(.ant-card-body) {
    overflow: visible;
  }

  /* Фиксированные заголовки и первый столбец */
  .fixed-table {
    position: relative;
  }

  .fixed-table thead th {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #fafafa !important;
  }

  .fixed-table tbody td:first-child {
    position: sticky;
    left: 0;
    z-index: 5;
    border-right: 2px solid #e8e8e8;
  }

  .fixed-table thead th:first-child {
    position: sticky;
    left: 0;
    z-index: 15;
    background: #fafafa !important;
    border-right: 2px solid #e8e8e8;
  }

  /* Поддержка горизонтальной прокрутки для полной ширины */
  body {
    overflow-x: auto;
  }
  </style> 